"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import * as z from "zod";

import { ImageUpload } from "@/components/image-upload";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useZodForm } from "@/hooks/use-zod-form";
import { STYLES } from "@/lib/styles";
import { orpc } from "@/orpc/react-query";

const formSchema = z.object({
	image: z.string().min(1, "Please upload an image to transform"),
	style: z.string().min(1, "Please select a style"),
	quantity: z.number().min(1, "Please select quantity").default(1),
	additionalInstructions: z.string().optional(),
});

interface TransformFormProps {
	defaultStyle?: string;
	showStyleSelector?: boolean;
}

export function TransformForm({
	defaultStyle,
	showStyleSelector = true,
}: TransformFormProps) {
	const router = useRouter();

	const { data: user } = useQuery(orpc.user.getMe.queryOptions());
	const credits = user?.credits || 0;

	const form = useZodForm(formSchema, {
		image: "",
		style: defaultStyle || STYLES[0].id,
		quantity: 1,
		additionalInstructions: "",
	});

	const quantity = form.watch("quantity");
	const image = form.watch("image");

	const { mutate: transformImage, isPending } = useMutation(
		orpc.user.transformImage.mutationOptions({
			onError: (error) => {
				toast.error(error.message);
			},
			onSuccess: () => {
				toast.success("Image transformation started!");
				router.push("/studio/history");
				form.reset();
			},
		}),
	);

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit((data) => {
					transformImage({
						...data,
						quantity: data.quantity,
					});
				})}
				className="space-y-8"
			>
				<FormField
					control={form.control}
					name="image"
					render={({ field }) => (
						<FormItem>
							<FormLabel className="text-lg font-medium">
								Upload your image
							</FormLabel>
							<p className="text-sm text-muted-foreground mb-4">
								Upload the image you want to transform. For best results, use a
								clear image with good lighting.
							</p>
							<FormControl>
								<ImageUpload
									disabled={credits < 1}
									value={field.value}
									onChange={field.onChange}
									className="w-full max-w-[150px] md:max-w-[200px] aspect-[2/3]"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{showStyleSelector && (
					<FormField
						control={form.control}
						name="style"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-lg font-medium">
									Choose your transformation style
								</FormLabel>
								<div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mt-4">
									{STYLES.map((style) => (
										<Button
											key={style.id}
											type="button"
											variant="outline"
											className={`flex flex-col items-center gap-2 p-2 h-auto ${
												field.value === style.id
													? "ring-2 ring-[#ff4d00] ring-offset-2 dark:ring-offset-gray-900"
													: ""
											}`}
											onClick={() => form.setValue("style", style.id)}
										>
											<div className="aspect-[2/3] relative w-full rounded-lg overflow-hidden">
												<Image
													src={style.after}
													alt={style.title}
													fill
													className="object-cover"
												/>
											</div>
											<p className="text-sm font-medium">{style.title}</p>
										</Button>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				<FormField
					control={form.control}
					name="quantity"
					render={({ field }) => (
						<FormItem>
							<FormLabel className="text-lg font-medium">
								How many images do you need?
							</FormLabel>
							<div className="flex items-center gap-2 mt-4">
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={() => {
										const current = field.value;
										if (current > 1) {
											form.setValue("quantity", current - 1);
										}
									}}
									disabled={quantity === 1}
								>
									<Minus className="w-4 h-4" />
								</Button>
								<div className="w-12 text-center font-medium">
									{field.value}
								</div>
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={() => {
										const current = Number(field.value);
										if (current < 4 && credits >= current + 1) {
											form.setValue("quantity", current + 1);
										}
									}}
									disabled={credits < Number(quantity) + 1}
								>
									<Plus className="w-4 h-4" />
								</Button>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="additionalInstructions"
					render={({ field }) => (
						<FormItem>
							<FormLabel className="text-lg font-medium mb-4">
								Additional Instructions
							</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									placeholder="Enter any additional instructions here..."
									className="min-h-24"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button
					type="submit"
					size="lg"
					disabled={
						credits < Number(quantity) ||
						form.formState.isSubmitting ||
						!form.formState.isValid ||
						isPending
					}
					className="w-full bg-[#ff4d00] hover:bg-[#ff4d00]/90 text-white dark:text-white text-base px-6 h-12 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{form.formState.isSubmitting || isPending ? (
						<>
							<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							Transforming...
						</>
					) : (
						"Transform Image"
					)}
				</Button>

				<p className="text-sm text-muted-foreground text-center">
					This request costs {quantity} credits. {credits} credits remaining.
				</p>
			</form>
		</Form>
	);
}
